import { useEffect, useMemo, useState } from 'react';
import { detectLeaksFromSeries, DEFAULT_DECLINE_PER_DAY } from '../lib/leakDetect';
import { getFleet } from '../lib/dataSource';

const LEAK_COLOR = '#d64545';

function sevColor(worst) {
  if (worst == null) return 'inherit';
  if (worst >= 60) return '#d64545';
  if (worst >= 35) return '#e0902a';
  return '#1baf7a';
}

/**
 * FleetLeaks — runs leak detection across ALL wells (from the precomputed
 * fleet_fill_rates.json) and shows a sortable summary so problem wells are
 * visible at a glance. Click a row to open that well.
 */
function FleetLeaks({ onSelectWell, deact }) {
  const [fleet, setFleet] = useState(null);
  const [err, setErr] = useState(false);
  const [windowN, setWindowN] = useState(5);
  const [dropPct, setDropPct] = useState(15);
  const [minFills, setMinFills] = useState(2);
  const [requirePersist, setRequirePersist] = useState(true);
  const [declinePctDay, setDeclinePctDay] = useState(DEFAULT_DECLINE_PER_DAY * 100);
  const [sortKey, setSortKey] = useState('events');

  useEffect(() => {
    getFleet()
      .then(setFleet)
      .catch(() => setErr(true));
  }, []);

  const rows = useMemo(() => {
    if (!fleet) return [];
    const opts = {
      window: windowN,
      dropThresh: dropPct / 100,
      declinePerDay: declinePctDay / 100,
      minFills,
      requirePersist,
    };
    return fleet.wells
      // Exclude wells that were deactivated as bad readings.
      .filter(w => !(deact && deact.isWellOff(w.deviceId)))
      .map(w => {
        // Prefer the water/gas ratio series (isolates real leaks) when the well
        // has gas data; otherwise fall back to the raw fill-rate series.
        const useWgr = Array.isArray(w.wgrSeries) && w.wgrSeries.length >= 8;
        const series = useWgr
          ? w.wgrSeries.map(([time, wgr]) => ({ t: new Date(time), time, rate: wgr }))
          : w.series.map(([time, rate]) => ({ t: new Date(time), time, rate }));
        const { events, rows: r } = detectLeaksFromSeries(series, opts);
        const flagged = r.filter(x => x.leak).length;
        const worst = events.length ? Math.max(...events.flatMap(e => e.points.map(p => p.dropPct))) : null;
        const lastEvent = events.length ? events[events.length - 1].startT : null;
        return {
          deviceId: w.deviceId,
          wellName: w.wellName,
          metric: useWgr ? 'W/G ratio' : 'fill rate',
          analyzed: r.filter(x => x.expected != null).length,
          events: events.length,
          flagged,
          worst,
          lastEvent,
        };
      });
  }, [fleet, windowN, dropPct, declinePctDay, minFills, requirePersist, deact && deact.deactivatedWells]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      if (sortKey === 'name') return a.wellName.localeCompare(b.wellName);
      if (sortKey === 'worst') return (b.worst || 0) - (a.worst || 0);
      if (sortKey === 'flagged') return b.flagged - a.flagged;
      if (sortKey === 'recent') return (b.lastEvent || 0) - (a.lastEvent || 0);
      return b.events - a.events; // default
    });
    return arr;
  }, [rows, sortKey]);

  const totals = useMemo(() => ({
    events: rows.reduce((s, r) => s + r.events, 0),
    wellsWithLeaks: rows.filter(r => r.events > 0).length,
    wells: rows.length,
  }), [rows]);

  if (err) return <div className="no-data">Fleet leak index not found. Run <code>node scripts/build_fleet_leak_index.js</code>.</div>;
  if (!fleet) return <div className="no-data">Loading fleet fill-rate data…</div>;

  return (
    <div className="leak-wrap">
      <div className="err-stats-row">
        <div className="err-stat">
          <div className="err-stat-label">Total leak events</div>
          <div className="err-stat-value" style={{ color: totals.events ? LEAK_COLOR : 'inherit' }}>{totals.events}</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Wells with leaks</div>
          <div className="err-stat-value">{totals.wellsWithLeaks} / {totals.wells}</div>
        </div>
      </div>

      <div className="error-plot-card">
        <div className="voldist-head">
          <h2>Fleet leak summary</h2>
          <div className="voldist-controls">
            <label>
              Window
              <select value={windowN} onChange={e => setWindowN(Number(e.target.value))}>
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={8}>8</option>
                <option value={12}>12</option>
              </select>
            </label>
            <label>
              Drop threshold
              <input type="number" value={dropPct} step={5} min={5}
                onChange={e => setDropPct(Number(e.target.value))} />
              <span>%</span>
            </label>
            <label>
              Min fills
              <select value={minFills} onChange={e => setMinFills(Number(e.target.value))}>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </label>
            <label>
              Natural decline
              <input type="number" value={declinePctDay} step={0.05}
                onChange={e => setDeclinePctDay(Number(e.target.value))} />
              <span>%/day</span>
            </label>
            <label title="Reject dips that recover on their own — a real leak does not heal itself">
              <input type="checkbox" checked={requirePersist}
                onChange={e => setRequirePersist(e.target.checked)} />
              Must not recover
            </label>
            <label>
              Sort
              <select value={sortKey} onChange={e => setSortKey(e.target.value)}>
                <option value="events">Most events</option>
                <option value="worst">Worst drop</option>
                <option value="flagged">Most flagged fills</option>
                <option value="recent">Most recent leak</option>
                <option value="name">Name</option>
              </select>
            </label>
          </div>
        </div>

        <table className="haul-table leak-table">
          <thead>
            <tr>
              <th>Well</th>
              <th>Metric</th>
              <th>Leak events</th>
              <th>Fills flagged</th>
              <th>Fills analyzed</th>
              <th>Worst drop</th>
              <th>Last leak</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr
                key={r.deviceId}
                className={r.events ? 'leak-row-clickable' : ''}
                onClick={() => onSelectWell && onSelectWell(String(r.deviceId))}
                style={{ cursor: onSelectWell ? 'pointer' : 'default' }}
              >
                <td>{r.wellName}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>{r.metric}</td>
                <td style={{ fontWeight: 600, color: r.events ? LEAK_COLOR : 'var(--text-muted)' }}>{r.events}</td>
                <td>{r.flagged}</td>
                <td>{r.analyzed}</td>
                <td style={{ fontWeight: 600, color: sevColor(r.worst) }}>
                  {r.worst != null ? `${r.worst.toFixed(0)}%` : '\u2014'}
                </td>
                <td>{r.lastEvent ? r.lastEvent.toLocaleDateString() : '\u2014'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="voldist-legend">
          <span>Click a well to open it in the Leak Events view.</span>
        </div>
      </div>
    </div>
  );
}

export default FleetLeaks;
